import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Users, Clock, Award, GitCompare, Eye, Send, SlidersHorizontal, X, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Stars, VerifiedBadge, StatusPill, inr, LoadingScreen, EmptyState } from "@/components/common";
import Seo from "@/components/Seo";

export default function Agencies() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false); // collapsed on phones; always open on lg+

  const [filters, setFilters] = useState({
    city: params.get("city") || "", category: params.get("category") || "",
    verified: false, min_rating: "", sort: "rating",
  });
  // Everything the homepage search collected, so the user sees what they asked for.
  const req = {
    pincode: params.get("pincode") || "", guards: Number(params.get("guards")) || 0,
    start: params.get("start") || "", duration: params.get("duration") || "",
  };
  const reqSummary = [filters.city || req.pincode, filters.category, req.guards ? `${req.guards} guards` : "",
    req.start ? `from ${req.start}` : "", req.duration].filter(Boolean).join(" \u00b7 ");
  // Only agencies that can staff the request (agencies with no capacity figure are kept).
  const shown = req.guards ? agencies.filter((ag) => !ag.available_guards || Number(ag.available_guards) >= req.guards) : agencies;

  const load = useCallback(async () => {
    setLoading(true);
    const q = {};
    if (filters.city) q.city = filters.city;
    if (params.get("pincode")) q.pincode = params.get("pincode");
    if (filters.category) q.category = filters.category;
    if (filters.verified) q.verified = true;
    if (filters.min_rating) q.min_rating = filters.min_rating;
    if (filters.sort) q.sort = filters.sort;
    try { const { data } = await api.get("/agencies", { params: q }); setAgencies(data); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/services").then((r) => setCats([...new Set(r.data.flatMap((c) => c.services))])).catch(() => {});
  }, []);

  const toggleCompare = (a) => {
    setCompare((prev) => prev.find((x) => x.id === a.id) ? prev.filter((x) => x.id !== a.id) : prev.length < 3 ? [...prev, a] : prev);
  };

  return (
    <div>
      <Seo
        title={filters.city
          ? `Security Guards & Agencies in ${filters.city} | GuardDekho.com`
          : "Find Security Guards & Verified Agencies in India | GuardDekho.com"}
        description={filters.city
          ? `Compare and hire PSARA-verified security agencies and security guards in ${filters.city}. View ratings, pricing and compliance on GuardDekho.com.`
          : "Browse and compare verified security agencies and security guards across India. Filter by city, service and rating, then hire in minutes on GuardDekho.com."}
      />
      <section className="bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <span className="brand-label text-brand">Find Security Agency</span>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter">Verified security agencies {filters.city && `in ${filters.city}`}</h1>
          <p className="mt-2 text-navy-300">{shown.length} agencies available</p>
          {reqSummary && <p className="mt-1 text-sm text-brand" data-testid="search-summary">Your search: {reqSummary}</p>}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-4 gap-8">
        {/* Filters */}
        <aside className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-navy-200 rounded-xl p-5">
            <button type="button" onClick={() => setFiltersOpen((o) => !o)} data-testid="filters-toggle"
              className="w-full flex items-center justify-between text-navy-900 font-heading font-semibold lg:pointer-events-none">
              <span className="flex items-center gap-2"><SlidersHorizontal size={16} /> Filters
                {(filters.city || filters.category || filters.verified) && (
                  <span className="lg:hidden text-[11px] font-normal text-navy-500">({[filters.city, filters.category, filters.verified && "Verified"].filter(Boolean).join(", ")})</span>
                )}
              </span>
              <ChevronDown size={18} className={`lg:hidden transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
            <div className={`${filtersOpen ? "block" : "hidden"} lg:block mt-4`} data-testid="filters-body">
            <label className="text-xs font-semibold text-navy-600">City</label>
            <Select value={filters.city || "all"} onValueChange={(v) => setFilters({ ...filters, city: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="filter-city"><SelectValue placeholder="All cities" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cities</SelectItem>
                {cities.map((c) => <SelectItem key={c.city} value={c.city}>{c.city}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="text-xs font-semibold text-navy-600 mt-4 block">Service</label>
            <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="filter-category"><SelectValue placeholder="All services" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All services</SelectItem>
                {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="text-xs font-semibold text-navy-600 mt-4 block">Sort by</label>
            <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
              <SelectTrigger className="mt-1" data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">Highest rated</SelectItem>
                <SelectItem value="experience">Most experienced</SelectItem>
                <SelectItem value="price">Lowest price</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 mt-5 cursor-pointer">
              <Checkbox checked={filters.verified} onCheckedChange={(v) => setFilters({ ...filters, verified: !!v })} data-testid="filter-verified" />
              <span className="text-sm text-navy-700">Verified only</span>
            </label>
            <Button variant="outline" className="w-full mt-5 border-navy-300" onClick={() => setFilters({ city: "", category: "", verified: false, min_rating: "", sort: "rating" })}>Clear filters</Button>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-3">
          {loading ? <LoadingScreen /> : shown.length === 0 ? (
            <EmptyState icon={Users} title="No agencies found" hint="Try adjusting your filters or search a different city." />
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {shown.map((a) => (
                <div key={a.id} className="card-lift bg-white border border-navy-200 rounded-xl p-5 flex flex-col" data-testid={`agency-card-${a.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-navy-900 text-white flex items-center justify-center font-heading font-bold text-lg">{a.name[0]}</div>
                      <div>
                        <h3 className="font-heading font-semibold text-navy-900 leading-tight">{a.name}</h3>
                        <div className="flex items-center gap-1 text-xs text-navy-500 mt-0.5"><MapPin size={12} /> {a.head_office?.city}, {a.head_office?.state}</div>
                      </div>
                    </div>
                    {a.featured && <Badge className="bg-brand hover:bg-brand text-white">Featured</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <VerifiedBadge verified={a.verified} />
                    {a.psara_status === "VALID" && <Badge variant="outline" className="border-navy-300 text-navy-600">PSARA</Badge>}
                    {a.gst_verified && <Badge variant="outline" className="border-navy-300 text-navy-600">GST</Badge>}
                  </div>
                  <div className="flex items-center gap-2 mt-3"><Stars value={a.rating} /><span className="text-sm text-navy-600">{a.rating} · {a.contracts_completed} contracts</span></div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center border-y border-navy-100 py-3">
                    <div><div className="font-heading font-bold text-navy-900">{a.years_experience}y</div><div className="text-[10px] uppercase text-navy-400">Experience</div></div>
                    <div><div className="font-heading font-bold text-navy-900">{a.total_guards}</div><div className="text-[10px] uppercase text-navy-400">Guards</div></div>
                    <div><div className="font-heading font-bold text-navy-900">{a.response_time_mins}m</div><div className="text-[10px] uppercase text-navy-400">Response</div></div>
                  </div>
                  <div className="text-sm text-navy-500 mt-3">From <span className="font-heading font-bold text-navy-900 text-base">{inr(a.price_per_guard_month)}</span>/guard/month</div>
                  <div className="flex gap-2 mt-4">
                    <Button variant="outline" className="flex-1 border-navy-300" onClick={() => navigate(`/agencies/${a.id}`)} data-testid={`view-${a.id}`}><Eye size={15} className="mr-1" /> Profile</Button>
                    <Button className="flex-1 bg-navy-900 hover:bg-navy-800" onClick={() => navigate("/post-requirement")} data-testid={`quote-${a.id}`}><Send size={15} className="mr-1" /> Quote</Button>
                    <Button variant="outline" size="icon" className={`border-navy-300 ${compare.find((x) => x.id === a.id) ? "bg-brand text-white border-brand" : ""}`} onClick={() => toggleCompare(a)} data-testid={`compare-${a.id}`} title="Add to compare"><GitCompare size={15} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Compare bar */}
      {compare.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-navy-900 text-white z-40 border-t border-navy-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 overflow-x-auto">
              <span className="brand-label text-brand shrink-0">Compare ({compare.length}/3)</span>
              {compare.map((a) => (
                <span key={a.id} className="flex items-center gap-1 bg-navy-800 rounded-full px-3 py-1 text-sm shrink-0">{a.name}<X size={13} className="cursor-pointer" onClick={() => toggleCompare(a)} /></span>
              ))}
            </div>
            <Button className="bg-brand hover:bg-brand-600 shrink-0" onClick={() => setShowCompare(true)} disabled={compare.length < 2} data-testid="open-compare-btn">Compare now</Button>
          </div>
        </div>
      )}

      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Agency comparison</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {[["Agency", (a) => a.name], ["City", (a) => a.head_office?.city], ["Verified", (a) => a.verified ? "Yes" : "No"],
                  ["Rating", (a) => `${a.rating}/5`], ["Experience", (a) => `${a.years_experience} years`], ["Guards", (a) => a.total_guards],
                  ["Price/guard/mo", (a) => inr(a.price_per_guard_month)], ["Contracts", (a) => a.contracts_completed],
                  ["Response time", (a) => `${a.response_time_mins} min`], ["Replacement SLA", (a) => `${a.replacement_sla_hours}h`],
                  ["PSARA", (a) => a.psara_status], ["Plan", (a) => a.subscription_plan]].map(([label, fn], i) => (
                  <tr key={label} className={i % 2 ? "bg-navy-50" : ""}>
                    <td className="p-3 font-semibold text-navy-700 w-40">{label}</td>
                    {compare.map((a) => <td key={a.id} className="p-3 text-navy-900">{fn(a)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
